import "@arco-design/web-react/es/TreeSelect/style";
import _TreeSelect from "@arco-design/web-react/es/TreeSelect";
import React, { useEffect, useState } from 'react';
import { fetchSource } from '@/utils/sourceRequest';
import { flowExcelStore } from '@/store/flowExcelStore';
import { replaceFieldName } from '@/utils/formItem';
import { findNodeByValue } from './utils';
/**
 * 跨组件实例共享的请求结果缓存（请求级 + 响应级双层去重）。
 *
 * - 响应级：key → resolved value，命中直接复用，不发请求。
 * - 请求级：key → in-flight Promise，命中则 await 同一个请求，避免并发 mount
 *   时 11 个实例同时各发起一次请求（race condition）。
 *
 * 行为：
 *  - 应用卸载后缓存仍保留（module-scope），适合短生命周期页面。
 *  - 不会自动失效，需要手动调用 `clearListSelectorCache()` 强制刷新。
 *  - 失败响应**不**写入响应级缓存（避免错误被"锁死"），但请求级 Promise 会清掉，
 *    下次能重试。
 */
const _listSelectorValueCache = new Map();
const _listSelectorPromiseCache = new Map();
export function clearListSelectorCache(key) {
  if (key === undefined) {
    _listSelectorValueCache.clear();
    _listSelectorPromiseCache.clear();
  } else {
    _listSelectorValueCache.delete(key);
    _listSelectorPromiseCache.delete(key);
  }
}
function buildCacheKey(source) {
  const params = source.params !== undefined ? JSON.stringify(source.params) : source.body !== undefined || source.data !== undefined ? JSON.stringify(source.body ?? source.data) : '';
  return `${source.method ?? 'get'}|${source.url}|${params}`;
}
function ListSelector(props) {
  const {
    onChange,
    name,
    value,
    source
  } = props;
  const [options, setOptions] = useState([]);
  useEffect(() => {
    if (value) {
      const item = findNodeByValue(value, options);
      onChange?.(value, item, name, false);
    }
  }, [value, name, options, onChange]);

  // source 变化时拉一次列表。并发 mount 多个组件时，所有实例共享同一个 Promise，
  // 避免 race condition 导致重复请求。
  useEffect(() => {
    if (!source?.url) return;
    const cacheKey = buildCacheKey(source);

    // 1. 响应级缓存命中：直接用，不发请求
    const cachedValue = _listSelectorValueCache.get(cacheKey);
    if (cachedValue) {
      setOptions(cachedValue);
      return;
    }

    // 2. 请求级缓存命中：复用 in-flight Promise
    let inflight = _listSelectorPromiseCache.get(cacheKey);
    if (!inflight) {
      inflight = fetchSource(source).then(res => {
        const list = Array.isArray(res) ? res : [];
        _listSelectorValueCache.set(cacheKey, list);
        return list;
      });
      _listSelectorPromiseCache.set(cacheKey, inflight);
      // 失败时清掉请求级缓存，让下次能重试
      inflight.catch(() => {
        if (_listSelectorPromiseCache.get(cacheKey) === inflight) {
          _listSelectorPromiseCache.delete(cacheKey);
        }
      });
    }
    let cancelled = false;
    inflight.then(list => {
      if (cancelled) return;
      setOptions(list);
    }).catch(e => {
      if (cancelled) return;
      // eslint-disable-next-line no-console
      console.error('[ListSelector] fetchSource failed', e);
    });
    return () => {
      cancelled = true;
    };
    // 用 cacheKey 字符串作为依赖：内容稳定时不会触发重复请求，
    // 避免每次 render source 引用变化导致无限请求。
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [source ? buildCacheKey(source) : '']);
  return /*#__PURE__*/React.createElement(_TreeSelect, {
    value: value,
    onChange: (value, item) => {
      const key = replaceFieldName(name, 'Name');
      flowExcelStore.form?.setFieldValue(key, item.trigger.title);
      onChange(value, item.trigger.dataRef, name, true);
    },
    treeData: options
  });
}
export default ListSelector;